// AlzijLogo.jsx
import { useState, useEffect, useRef } from 'react';

/**
 * Al Zij DiGi logo component with two-phase animation:
 * - Phase 1 (0–50px scroll): text slides off
 * - Phase 2 (50–100px scroll): boxes shrink into one
 * Also responds to `forceAnimate` to trigger same effect (e.g. on menu open).
 * The `isHomePage` prop controls whether the logo should run animations based on scroll.
 */
export default function AlzijLogo({ forceAnimate = false, isHomePage = true }) {
  const [scrolled, setScrolled] = useState(false);
  const [textProgress, setTextProgress] = useState(isHomePage ? 0 : 1);
  const [boxProgress, setBoxProgress] = useState(isHomePage ? 0 : 1);
  const prevScrollY = useRef(0);

  // Handle route changes specifically
  useEffect(() => {
    // When not on home page, always force final animation states
    if (!isHomePage) {
      setTextProgress(1);
      setBoxProgress(1);
    } else if (!scrolled && !forceAnimate) {
      // When on home page and not scrolled/forced, reset to initial state
      requestAnimationFrame(() => {
        setTextProgress(0);
        setBoxProgress(0);
      });
    }
  }, [isHomePage]); // This effect runs when the route changes

  // Scroll effect - only active on home page
  useEffect(() => {
    if (!isHomePage) {
      return;
    }
    
    const onScroll = () => {
      const y = window.scrollY;
      prevScrollY.current = y;
      setScrolled(y > 100);

      if (y <= 50) {
        setTextProgress(y / 50);
        setBoxProgress(0);
      } else {
        setTextProgress(1);
        setBoxProgress(Math.min((y - 50) / 50, 1));
      }
    };
    
    window.addEventListener('scroll', onScroll);
    onScroll();
    
    return () => window.removeEventListener('scroll', onScroll);
  }, [isHomePage]);

  // Handle scroll state and forceAnimate changes
  useEffect(() => {
    if (!isHomePage) {
      return;
    }
    
    if (forceAnimate) {
      setTextProgress(1);
      setBoxProgress(1);
    } else if (!scrolled) {
      requestAnimationFrame(() => {
        setTextProgress(0);
        setBoxProgress(0);
      });
    }
  }, [forceAnimate, scrolled, isHomePage]);

  // Force the scale based on route first, then consider scroll and forceAnimate
  // This ensures non-Home pages always have the small scale regardless of scroll state
  const active = !isHomePage || scrolled || forceAnimate;
  const scale = !isHomePage ? 0.5 : (active ? 0.5 : 0.7);
  const color = !isHomePage ? '#000' : (active ? '#000' : '#fff');
  const textOffset = -250 * textProgress;

  // Geometry constants
  const BOX_X = 30;
  const BOX_Y = 30;
  const TEXT_SIZE = 80;
  const MARGIN_Y = 20;
  const LINE_H = TEXT_SIZE + MARGIN_Y * 2; // 120
  const TEXT_X = BOX_X + 30;
  const FONT = "'Space Mono', monospace";

  const BOX_W_AL = 80 + 30 * 2 + 20;
  const BOX_W_ZIJ = 120 + 30 * 2 + 20;
  const BOX_W_DIGI = 180 + 30 * 2;

  const BASE_OFFSET = MARGIN_Y + TEXT_SIZE * 0.8; // 84
  const Y_AL = BOX_Y + BASE_OFFSET;
  const Y_ZIJ = Y_AL + LINE_H;
  const Y_DIGI = Y_ZIJ + LINE_H;

  const AL_H = LINE_H;
  const ZIJ_H = LINE_H * 2 - (LINE_H * boxProgress);
  const DIGI_H = LINE_H * 3 - (LINE_H * 2 * boxProgress);

  const strokeW = 5;
  const mainFrameStrokeW = 6;
  const zijBoxStrokeW = 5;
  const alBoxStrokeW = 4;

  const H_ALL = LINE_H * 3;
  const mainH = H_ALL - ((H_ALL - (AL_H + 20)) * boxProgress) + strokeW * 2;

  const mainX = BOX_X - mainFrameStrokeW;
  const mainY = BOX_Y - mainFrameStrokeW;
  const mainW = BOX_W_DIGI + mainFrameStrokeW * 2;

  useEffect(() => {
    // Add forceAnimate and scrolled to dependencies
    if (forceAnimate || scrolled) {
      // Your animation logic here
    }
  }, [forceAnimate, scrolled]); // Add missing dependencies

  return (
    <div
      className="relative flex justify-center items-center p-4 select-none drop-shadow-[4px_5px_0_rgba(0,0,0,0.2)]"
      style={{ 
        userSelect: 'none',
        pointerEvents: 'none' // Disable all pointer events at this level
      }}
    >
      {/* Original SVG with pointer-events disabled */}
      <svg
        viewBox="0 0 400 500"
        className="transition-all duration-300 ease-out"
        style={{ 
          width: 260, 
          height: 350, 
          transform: `scale(${scale})`,
          pointerEvents: 'none' 
        }}
      >
        {/* Main frame */}
        <rect
          x={mainX}
          y={mainY}
          width={mainW}
          height={mainH}
          stroke={color}
          strokeWidth={mainFrameStrokeW}
          fill="none"
          vectorEffect="non-scaling-stroke"
          className="transition-all duration-300 ease-out"
        />

        {/* Zij box */}
        <rect
          x={BOX_X - zijBoxStrokeW}
          y={BOX_Y - zijBoxStrokeW}
          width={BOX_W_ZIJ}
          height={ZIJ_H + zijBoxStrokeW * 2}
          stroke={color}
          strokeWidth={zijBoxStrokeW}
          fill="none"
          className="transition-all duration-300 ease-out"
        />

        {/* Al box */}
        <rect
          x={BOX_X - alBoxStrokeW}
          y={BOX_Y - alBoxStrokeW}
          width={BOX_W_AL}
          height={AL_H + alBoxStrokeW * 2}
          stroke={color}
          strokeWidth={alBoxStrokeW}
          fill="none"
          className="transition-colors duration-300 ease-out"
        />

        {/* Clips */}
        <defs>
          <clipPath id="alClip">
            <rect
              x={BOX_X}
              y={BOX_Y}
              width={BOX_W_AL - alBoxStrokeW * 2}
              height={AL_H}
            />
          </clipPath>
          <clipPath id="zijClip">
            <rect
              x={BOX_X}
              y={BOX_Y + LINE_H}
              width={BOX_W_ZIJ - zijBoxStrokeW * 2}
              height={LINE_H - zijBoxStrokeW}
            />
          </clipPath>
          <clipPath id="digiClip">
            <rect
              x={BOX_X}
              y={BOX_Y + LINE_H * 2}
              width={BOX_W_DIGI - mainFrameStrokeW * 2}
              height={LINE_H - mainFrameStrokeW}
            />
          </clipPath>
        </defs>

        {/* Text - Using transform instead of x position for smoother transitions */}
        <g fill={color} className="transition-colors duration-300 ease-out">
          <g clipPath="url(#alClip)">
            <text
              x={TEXT_X}
              y={Y_AL}
              fontFamily={FONT}
              fontSize={TEXT_SIZE}
              fontWeight="700"
              style={{
                transform: `translateX(${textOffset}px)`,
                transition: 'transform 300ms ease-out',
                transformOrigin: 'center',
                willChange: 'transform'
              }}
            >
              Al
            </text>
          </g>
          <g clipPath="url(#zijClip)">
            <text
              x={TEXT_X}
              y={Y_ZIJ}
              fontFamily={FONT}
              fontSize={TEXT_SIZE}
              fontWeight="700"
              style={{
                transform: `translateX(${textOffset}px)`,
                transition: 'transform 300ms ease-out',
                transformOrigin: 'center',
                willChange: 'transform'
              }}
            >
              Zij
            </text>
          </g>
          <g clipPath="url(#digiClip)">
            <text
              x={TEXT_X}
              y={Y_DIGI}
              fontFamily={FONT}
              fontSize={TEXT_SIZE}
              fontWeight="700"
              style={{
                transform: `translateX(${textOffset}px)`,
                transition: 'transform 300ms ease-out',
                transformOrigin: 'center',
                willChange: 'transform'
              }}
            >
              DiGi
            </text>
          </g>
        </g>
      </svg>
    </div>
  );
}